





























































































































import { Component, Vue, PropSync, Watch, Prop } from "vue-property-decorator";
import {
  ATMAsset,
  ATMPair,
  TradeStrategy,
  TradeSide
} from "@foxone/api/src/models/market";
import { Asset } from "@foxone/api/src/models/wallet/mixin";
import { State } from "vuex-class";
import { ERROR } from "trade/constants";
import utils from "trade/utils";
import { EXCHANGES } from "../../constants";
import PriceImpactModal from "./PriceImpactModal.vue";
import { Colors } from "@/types";

@Component({
  components: {
    PriceImpactModal
  }
})
class AssetsSelector extends Vue {
  @PropSync("offerAssetValue") offerAsset!: ATMAsset | null;

  @PropSync("wantedAssetValue") wantedAsset!: ATMAsset | null;

  @PropSync("offerAmountValue") offerAmount;

  @PropSync("wantedAmountValue") wantedAmount;

  @Prop() assets!: ATMAsset[];

  @Prop() estimatedRate;

  @Prop({ default: true }) wantedSelectable;

  @Prop({ default: true }) offerSelectable;

  @Prop({ default: false }) preordering!: boolean;

  @Prop() priceImpact;

  @Prop() avgPrice;

  @Prop() side;

  @Prop() pair;

  @State(state => state.trade.pairs) pairs!: ATMPair[];

  offerAssetDetail: Asset | null = null;

  wantedAssetDetail: Asset | null = null;

  offerAmountErrorMessage = "";

  loading = false;

  priceReverse = false;

  order: any = null;

  get meta() {
    const amount = Number(this.offerAmount);
    const balance = this.offerAssetDetail?.balance;
    const offerAssetSymbol = this.offerAsset?.symbol ?? "";
    const wantAssetSymbol = this.wantedAsset?.symbol ?? "";

    const min = this.limit?.min;
    const max = this.limit?.max;
    const enabled = max !== undefined && min !== undefined && +max > +min;
    const valid = enabled && amount >= +min && amount <= +max;

    const minText = min || "-";
    const maxText = max || "-";
    const limitText = `${minText} ~ ${maxText} ${offerAssetSymbol}`;
    const limitErrorText = this.$t("common.amount.out-of-limit");

    let rateText = "";
    let reverseRateText = "";
    if (wantAssetSymbol && offerAssetSymbol) {
      rateText = `1 ${offerAssetSymbol} ≈ ${this.estimatedRate.price} ${wantAssetSymbol}`;
      reverseRateText = `1 ${wantAssetSymbol} ≈ ${this.estimatedRate.reversePrice} ${offerAssetSymbol}`;
    }

    let avgPriceText = "-";
    if (this.avgPrice) {
      avgPriceText = `1 ${offerAssetSymbol}  ≈ ${this.avgPrice} ${wantAssetSymbol}`;
    }

    let priceImpactText = "-";
    if (this.avgPrice) {
      priceImpactText = this.$common.utils.number.toPercent(
        this.priceImpact,
        true,
        2
      );
    }

    let priceImpactColor = Colors.GREEN;
    if (this.priceImpact < 0 && this.priceImpact > -0.02) {
      priceImpactColor = Colors.WARNING;
    } else if (this.priceImpact <= -0.02) {
      priceImpactColor = Colors.RED;
    }

    const canSwitch = this.wantedAsset || this.offerAsset;
    const wantedAssetPrice = this.wantedAssetDetail?.price_usd ?? "";
    const wantedAssetUsdAmount = +wantedAssetPrice * (+this.wantedAmount || 0);
    const wantedAssetLegal = this.$common.utils.currency.fiat(this, {
      from: "USD",
      value: wantedAssetUsdAmount
    });
    const amountText = `${this.offerAmount} ${offerAssetSymbol} (≈ ${wantedAssetLegal})`;

    const offerRules = [
      v => {
        if (enabled) {
          if (v === "0" || !v) {
            return true;
          }
          return (min && +v >= +min && max && +v <= +max) || limitErrorText;
        }
        return true;
      }
    ];

    return {
      valid,
      enabled,
      limitText,
      reverseRateText,
      offerRules,
      canSwitch,
      balance,
      avgPriceText,
      priceImpactText,
      priceImpactColor,
      amountText,
      rateText: this.priceReverse ? reverseRateText : rateText,
      minOffer: min,
      maxOffer: max,
      inputDisabled: +min >= +max || +max <= 0,
      submitDisabaled: !valid,
      balanceText: balance ? `${balance} ${offerAssetSymbol}` : "",
      wantedLabel: this.$t("trade.want"),
      wantedAssets: this.getAvailableAssets(this.offerAsset || ""),
      wantedPrecision: this.wantedAsset?.precision ?? 0,
      offerLabel: this.$t("trade.offer"),
      offerAssets: this.getAvailableAssets(this.wantedAsset || ""),
      offerPrecision: this.offerAsset?.precision ?? 0
    };
  }

  get limit() {
    if (!this.pair || !this.offerAsset) return null;
    const { base, quote } = this.pair;
    const asset = base.symbol === this.offerAsset.symbol ? base : quote;
    return {
      min: (asset.minimums && asset.minimums.market) || asset.min,
      max: asset.max
    };
  }

  get fee() {
    const toPrecision = this.$common.utils.number.toPrecision;
    if (
      !this.pair ||
      !this.offerAsset ||
      !this.wantedAsset ||
      !this.wantedAmount
    )
      return "-";
    const { base, bid_fee, ask_fee } = this.pair;
    const rate = base.symbol === this.offerAsset.symbol ? ask_fee : bid_fee;
    const amount = toPrecision(
      Number(rate) * Number(this.wantedAmount),
      base.precision
    ).toString();
    return `≈${amount} ${this.wantedAsset.symbol}`;
  }

  get exchange() {
    return EXCHANGES.find(x => x.id === this.pair?.exchange ?? "");
  }

  mounted() {
    this.$nextTick(() => {
      if (!this.offerAsset) {
        this.offerAsset = this.meta.offerAssets[0] || "";
      }

      this.$nextTick(() => {
        if (!this.wantedAsset) {
          this.wantedAsset = this.meta.wantedAssets[0] || "";
        }
      });
    });
  }

  @Watch("offerAsset")
  async handleOfferChange(value) {
    this.offerAssetDetail = null;
    const detail = await this.requestAsset(value);
    this.offerAssetDetail = detail;
  }

  @Watch("wantedAsset", { immediate: true })
  async handleWantedChange(value) {
    this.wantedAssetDetail = null;
    const detail = await this.requestAsset(value);
    this.wantedAssetDetail = detail;
  }

  @Watch("wantedAsset")
  @Watch("offerAsset")
  @Watch("offerAmount")
  handleOfferFieldChange() {
    setTimeout(() => {
      const form: any = this.$refs.form;
      if (!form) return;
      this.offerAmountErrorMessage = form.inputs[0].messagesToDisplay[0];
    }, 200);
  }

  async handleSubmit(checkDepth = true) {
    const form: any = this.$refs.form;
    const valid = form.validate();
    if (!this.wantedAsset || !this.offerAsset || !this.offerAmount) return;
    if (valid) {
      this.loading = true;
      let shouldWarning = false;
      if (checkDepth) {
        shouldWarning = await this.checkDepth();
      }
      if (!shouldWarning) {
        await this.requestPutOrder();
      }
      this.loading = false;
    }
  }

  handleSwitch() {
    const tmp = this.wantedAsset;
    this.wantedAsset = this.offerAsset;
    this.offerAsset = tmp;
    this.validateForm();
  }

  validateForm() {
    const form: any = this.$refs.form;
    form && form.validate();
  }

  getAvailableAssets(tradeAsset: ATMAsset | "") {
    let assets: ATMAsset[] = [];
    if (tradeAsset === "") {
      assets = this.assets;
    } else {
      assets = this.assets.filter(
        asset => asset.asset_id !== tradeAsset.asset_id
      );
      assets = assets.filter(asset => {
        let contain = false;
        for (const pair of this.pairs) {
          const { quote, base } = pair;
          if (
            quote.asset_id === tradeAsset.asset_id &&
            base.asset_id === asset.asset_id
          )
            contain = true;
          if (
            quote.asset_id === asset.asset_id &&
            base.asset_id === tradeAsset.asset_id
          )
            contain = true;
        }
        return contain;
      });
    }
    const sorts = ["pUSD", "BOX", "BTC", "ETH", "XIN", "EOS", "XMR"].reverse();
    assets = assets.sort((a, b) => {
      const ia = sorts.indexOf(a.symbol);
      const ib = sorts.indexOf(b.symbol);
      return ia > ib ? -1 : 1;
    });
    return assets.map(asset => ({ id: asset.asset_id, ...asset }));
  }

  async requestPutOrder() {
    try {
      if (!this.pair || !this.side) return;
      const order = await this.$api.market.putOrder({
        pair: this.pair.symbol,
        strategy: TradeStrategy.MARKET,
        side: this.side,
        quantity: this.offerAmount
      });
      this.order = order;
      const payment = await this.$api.market.requestPayOrder(order.id + "");
      utils.order.payment(this, payment, order.id + "", () => {
        this.handleToOrder();
      });
    } catch (error) {
      if (error.code === ERROR.HAS_UNPAID_AUTO_TRADE_ORDER) {
        this.$common.utils.helper.message(
          this,
          {
            title: this.$t("common.order.unpaid-exit") as string,
            description: this.$t(
              "common.order.unpaid-exit.description"
            ) as string,
            ok: this.$t("trade.view-order") as string,
            cancel: ""
          },
          {
            onOK: () => {
              this.handleToOrders();
            }
          }
        );
        return;
      }
      this.$common.utils.helper.errorHandler(this, error);
    }
    this.loading = false;
  }

  async checkDepth() {
    const data = {
      amount: this.offerAmount,
      side: this.side,
      symbol: this.pair?.symbol
    };
    const {
      market_price,
      filled_avg_price
    } = await this.$api.market.validateDepth(data);
    const impact = (filled_avg_price - market_price) / market_price;
    const shouldWarning =
      this.side === TradeSide.BID ? impact >= 0.02 : impact <= -0.02;
    if (shouldWarning) {
      const impactModal = this.$refs.impactModal as any;
      impactModal.show({
        marketPrice: market_price,
        filledAvgPrice: filled_avg_price
      });
    }
    return shouldWarning;
  }

  handleBalanceClick() {
    this.offerAmount = this.meta.balance || "";
  }

  handleToOrders() {
    this.$helper.toFusionPath(this, "trade", { name: "orders" });
  }

  handleToOrder() {
    if (this.order && this.order.id) {
      this.$helper.toFusionPath(this, "trade", {
        name: "orders-id",
        params: { id: this.order.id }
      });
    }
  }

  async requestAsset(asset) {
    if (!asset) return null;
    const data = await this.$api.wallet.mixin.getAsset(asset.asset_id);
    return data;
  }
}
export default AssetsSelector;
